import { useApi } from "@backstage/core-plugin-api";
import {
  opsgenieApiRef,
  OpsgenieIncident,
} from "@internal/plugin-opsgenie-api";
import { CardContent } from "@material-ui/core";
import React, { useEffect, useState } from "react";
import { ADFEntity } from "@atlaskit/adf-utils/types";
import { traverse } from "@atlaskit/adf-utils/traverse";
import { Link, Progress } from "@backstage/core-components";

export interface Props {
  incident: OpsgenieIncident;
}

export const IncidentSummary: React.FC<Props> = ({ incident }): JSX.Element => {
  const api = useApi(opsgenieApiRef);
  const [postMortemId, setPostMortemId] = useState<string>();
  const [executiveSummary, setExecutiveSummary] = useState<any[]>();

  useEffect(() => {
    (async () => {
      const pmReq = await api.getPostMortemIdByIncidentId({
        incidentId: incident.id,
      });
      if (pmReq.status === "error") {
        return;
      }

      const pmId = pmReq.data.data;

      if (pmId) {
        setPostMortemId(pmId);
      }
    })();
  }, [api, setPostMortemId, incident]);

  useEffect(() => {
    (async () => {
      const parse = (document: any) =>
        traverse(document, {
          /** These are being ignored so we don't get crashes
           *
           * Here's the full list of nodes: https://developer.atlassian.com/cloud/jira/platform/apis/document/nodes/table/
           */
          blockQuote: (_node, _parent) => false,
          codeBlock: (_node, _parent) => false,
          emoji: (_node, _parent) => false,
          inlineCard: (_node, _parent) => false,
          media: (_node, _parent) => false,
          mediaGroup: (_node, _parent) => false,
          mediaSingle: (_node, _parent) => false,
          mention: (_node, _parent) => false,
          orderedList: (_node, _parent) => false,
          panel: (_node, _parent) => false,
          rule: (_node, _parent) => false,
          table: (_node, _parent) => false,
          tableRow: (_node, _parent) => false,
          tableCell: (_node, _parent) => false,
          tableHeader: (_node, _parent) => false,
          hardBreak: (_node, _parent) => {
            return <br />;
          },
          text: (node, _parent) => {
            return <span>{node.text}</span>;
          },
          heading: (node, _parent) => {
            const content = parse({
              version: 1,
              type: "doc",
              content: node.content,
            });

            if (!(content as ADFEntity)?.content) {
              return false;
            }

            return (
              <h6>{(content as ADFEntity).content as React.ReactNode[]}</h6>
            );
          },
          listItem: (node, _parent) => {
            const content = parse(node.content);
            const innerContent = parse({
              version: 1,
              type: "doc",
              content: (content as ADFEntity)[0].content,
            });
            return (
              <li>
                {(innerContent as ADFEntity).content as React.ReactNode[]}
              </li>
            );
          },
          paragraph: (node, _parent) => {
            const content = parse({
              version: 1,
              type: "doc",
              content: node.content,
            });
            if (!(content as ADFEntity)?.content) {
              return false;
            }
            return (
              <p>
                {
                  (content as ADFEntity).content?.filter(
                    (e: any) => e
                  ) as React.ReactNode[]
                }
              </p>
            );
          },
          bulletList: (node, _parent) => {
            const content = parse({
              version: 1,
              type: "doc",
              content: node.content,
            });
            return (
              <ul>{(content as ADFEntity).content as React.ReactNode[]}</ul>
            );
          },
        });

      if (postMortemId) {
        const req = await api.getPostMortem({ postMortemId });
        const pm = req.data.data;

        const es = parse(pm.executiveSummary);

        if (es) {
          const converted = es?.content?.filter((e) => e).flat();
          setExecutiveSummary(converted);
        } else {
          setExecutiveSummary([
            <div>
              Uh oh, we couldn't find a Post Mortem attached to that
              Incident.... did you make sure to{" "}
              <Link to="/opsgenie/post-mortem-form">add it in Radiator</Link>?
            </div>,
          ]);
        }
      } else {
        setExecutiveSummary([
          <div>
            Uh oh, we couldn't find a Post Mortem attached to that Incident....
            did you make sure to{" "}
            <Link to="/opsgenie/post-mortem-form">add it in Radiator</Link>?
          </div>,
        ]);
      }
    })();
  }, [api, postMortemId]);

  const summary = executiveSummary ? (
    <div>{executiveSummary}</div>
  ) : (
    <Progress />
  );

  return (
    <div>
      <CardContent>{summary}</CardContent>
    </div>
  );
};
